// context/AuthContext.js

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosClient from '../api/axiosClient';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation('auth');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const [followingSet, setFollowingSet] = useState(null);
  const [followingReady, setFollowingReady] = useState(false);

  // Estado de Stripe (hasStripeAccount, chargesEnabled, etc.)
  const [stripeStatus, setStripeStatus] = useState({
    hasStripeAccount: false,
    accountAccessRevoked: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false,
    accountId: null,
    capabilities: null,
    loading: true,
  });

  const navigate = useNavigate();

  /**
   * Carga o refresca la información del usuario logueado (user),
   * llamando a /api/users/profile
   */
  const refreshUserData = useCallback(async () => {
    if (!token) {
      setUser(null);
      return null;
    }

    try {
      const response = await axiosClient.get('/api/users/profile');

      if (response.data.success) {
        const updatedUser = response.data.user;
        setUser(updatedUser);
        return updatedUser;
      } else {
        setUser(null);
        return null;
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
      if (error?.response?.status === 401) {
        // Token no válido o expirado -> cerrar sesión
        localStorage.removeItem('authToken');
        setToken(null);
        setUser(null);
      }
      return null;
    }
  }, [token]);

  /**
   * Refresca el estado de Stripe para saber si
   * hasStripeAccount (true|false) y chargesEnabled (true|false)
   */
  const refreshStripeStatus = useCallback(async (userId) => {
    setStripeStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await axiosClient.get(`/api/stripe/refresh-account-status?userId=${userId}`);
      const nextStatus = {
        loading: false,
        hasStripeAccount: response.data.hasStripeAccount,
        accountAccessRevoked: !!response.data.accountAccessRevoked,
        chargesEnabled: response.data.chargesEnabled,
        payoutsEnabled: response.data.payoutsEnabled,
        detailsSubmitted: response.data.detailsSubmitted,
        accountId: response.data.accountId,
        capabilities: response.data.capabilities,
      };

      setStripeStatus(nextStatus);
      setUser(prev => {
        if (!prev) return prev;

        const nextStripeFields = {
          stripeConnectedAccountId: response.data.accountId || null,
          chargesEnabled: !!response.data.chargesEnabled,
          stripeAccountPayoutsEnabled: !!response.data.payoutsEnabled,
          stripeAccountCapabilities: response.data.capabilities,
        };
        const capabilitiesChanged = JSON.stringify(prev.stripeAccountCapabilities || null) !== JSON.stringify(nextStripeFields.stripeAccountCapabilities || null);

        if (
          prev.stripeConnectedAccountId === nextStripeFields.stripeConnectedAccountId &&
          prev.chargesEnabled === nextStripeFields.chargesEnabled &&
          prev.stripeAccountPayoutsEnabled === nextStripeFields.stripeAccountPayoutsEnabled &&
          !capabilitiesChanged
        ) {
          return prev;
        }

        return {
          ...prev,
          ...nextStripeFields,
        };
      });
      return nextStatus;
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
      const fallbackStatus = {
        loading: false,
        hasStripeAccount: false,
        accountAccessRevoked: false,
        chargesEnabled: false,
        payoutsEnabled: false,
        detailsSubmitted: false,
        accountId: null,
        capabilities: null,
      };
      setStripeStatus(fallbackStatus);
      return fallbackStatus;
    }
  }, []);

  /**
   * Carga inicial del usuario si hay token en localStorage
   */
  useEffect(() => {
    const init = async () => {
      if (token) {
        const u = await refreshUserData();
        if (u?.id) {
          try {
            const { data } = await axiosClient.get(`/api/users/${u.id}/following?limit=1000`);
            const set = new Set((data.following || []).map(f => String(f.followeeId?._id || f.followeeId)));
            setFollowingSet(set);
          } catch (e) {
            setFollowingSet(new Set());
          } finally {
            setFollowingReady(true);
          }
        } else {
          setFollowingSet(new Set());
          setFollowingReady(true);
        }
      }
      setLoading(false);
    };
    init();
  }, [token, refreshUserData]);

  /**
   * Iniciar sesión por email (Magic Link)
   */
  const login = async (email) => {
    // Verificar que se proporcione un email válido
    if (!email || typeof email !== 'string' || !email.trim()) {
      console.warn('Login function called without valid email');
      return;
    }

    try {
      await axiosClient.post('/api/users/check-or-register', { email });
      toast.success(t('authContext.magicLinkSuccess'));
    } catch (error) {
      console.error('Error logging in', error);
      toast.error(t('authContext.magicLinkError'));
    }
  };

  /**
   * Logout
   */
  const logout = async () => {
    try {
      await axiosClient.post('/api/users/logout');
      localStorage.removeItem('authToken');
      setToken(null);
      setUser(null);
      setFollowingSet(null);
      setFollowingReady(false);
      navigate('/discover');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error(t('authContext.logoutError'));
    }
  };

  const isFollowing = useCallback((targetUserId) => {
    if (!followingSet) return false;
    return followingSet.has(String(targetUserId));
  }, [followingSet]);

  const follow = useCallback(async (targetUserId) => {
    await axiosClient.post(`/api/users/${targetUserId}/follow`);
    setFollowingSet(prev => {
      const next = new Set(prev || []);
      next.add(String(targetUserId));
      return next;
    });
  }, []);

  const unfollow = useCallback(async (targetUserId) => {
    await axiosClient.delete(`/api/users/${targetUserId}/follow`);
    setFollowingSet(prev => {
      const next = new Set(prev || []);
      next.delete(String(targetUserId));
      return next;
    });
  }, []);

  const refreshFollowing = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await axiosClient.get(`/api/users/${user.id}/following?limit=1000`);
      const set = new Set((data.following || []).map(f => String(f.followeeId?._id || f.followeeId)));
      setFollowingSet(set);
    } catch (e) { }
  }, [user?.id]);


  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      token,
      setToken,
      loading,
      login,
      logout,
      refreshUserData,
      stripeStatus,
      refreshStripeStatus,
      isAuthenticated: !!user && !!token,
      followingSet,
      followingReady,
      isFollowing,
      follow,
      unfollow,
      refreshFollowing
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
